import React from 'react';
import { Search, Calendar, RotateCcw, Download } from 'lucide-react';

export const FilterBar = ({
  searchPlaceholder = "Search...",
  searchTerm,
  setSearchTerm,
  filters = [], // Array of { id, value, onChange, options: [{ value, label }] }
  showDateRange = false,
  showTimeRangeToggles = false, // T W M Y
  fromDate,
  toDate,
  onDateChange,     // (field: 'fromDate'|'toDate', value: string) => void
  activePreset,     // 'today' | 'this_week' | 'this_month' | 'this_year' | 'all'
  onDatePreset,     // (preset: string) => void
  onReset,
  onExport,
  exportLabel = "Export",
  onAddClick,
  addLabel = "New",
  isExporting,
}) => {
  const PRESETS = [
    { key: 'today',      label: 'T' },
    { key: 'this_week',  label: 'W' },
    { key: 'this_month', label: 'M' },
    { key: 'this_year',  label: 'Y' },
  ];

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 py-1">
      {/* Left Filter Inputs */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Search Box */}
        {setSearchTerm && (
          <div className="relative flex items-center">
            <Search size={15} className="absolute left-3 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={searchPlaceholder}
              className="h-9 pl-9 pr-3 text-[13px] bg-white border border-slate-200 outline-none focus:border-slate-400 text-slate-700 font-medium shadow-xs w-60 transition-all"
            />
          </div>
        )}

        {/* Dynamic Select Filters */}
        {filters.map((filter) => (
          <select
            key={filter.id}
            value={filter.value}
            onChange={(e) => filter.onChange(e.target.value)}
            className="h-9 px-3 border border-slate-200 bg-white text-slate-700 text-xs font-medium focus:outline-none focus:border-slate-400 cursor-pointer shadow-xs"
          >
            {filter.options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        ))}

        {/* Date Range */}
        {showDateRange && (
          <>
            <div className="relative flex items-center">
              <Calendar size={14} className="absolute left-2.5 text-slate-400 pointer-events-none" />
              <input
                type="date"
                value={fromDate || ''}
                onChange={(e) => onDateChange && onDateChange('fromDate', e.target.value)}
                className="h-9 pl-8 pr-2 border border-slate-200 bg-white text-slate-700 text-xs font-medium focus:outline-none focus:border-slate-400 cursor-pointer shadow-xs"
                title="From Date"
              />
            </div>
            <div className="relative flex items-center">
              <Calendar size={14} className="absolute left-2.5 text-slate-400 pointer-events-none" />
              <input
                type="date"
                value={toDate || ''}
                onChange={(e) => onDateChange && onDateChange('toDate', e.target.value)}
                className="h-9 pl-8 pr-2 border border-slate-200 bg-white text-slate-700 text-xs font-medium focus:outline-none focus:border-slate-400 cursor-pointer shadow-xs"
                title="To Date"
              />
            </div>
          </>
        )}

        {/* T W M Y Quick Preset Buttons */}
        {showTimeRangeToggles && (
          <div className="flex items-center gap-1 shrink-0">
            {PRESETS.map(({ key, label }) => {
              const isActive = activePreset === key;
              return (
                <button
                  key={key}
                  onClick={() => onDatePreset && onDatePreset(isActive ? 'all' : key)}
                  className={`w-8 h-8 flex items-center justify-center border text-xs font-medium transition-colors shadow-xs cursor-pointer
                    ${isActive
                      ? 'bg-brand-dark text-white border-brand-dark'
                      : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                    }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}

        {/* Reset Button */}
        {onReset && (
          <button
            onClick={onReset}
            className="w-9 h-9 flex items-center justify-center border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 transition-colors shrink-0 shadow-xs cursor-pointer"
            title="Reset Filters"
          >
            <RotateCcw size={15} />
          </button>
        )}
      </div>

      {/* Right Actions */}
      <div className="flex items-center gap-2 ml-auto">
        {onExport && (
          <button
            onClick={onExport}
            disabled={isExporting}
            className="flex items-center gap-2 h-9 px-4 border border-slate-200 bg-white text-slate-700 text-xs font-semibold hover:bg-slate-50 transition-colors shrink-0 shadow-xs cursor-pointer disabled:opacity-60"
          >
            <Download size={14} className="text-slate-600" />
            <span>{isExporting ? 'Exporting...' : exportLabel}</span>
          </button>
        )}

        {onAddClick && (
          <button
            onClick={onAddClick}
            className="flex items-center gap-2 h-9 px-4 border border-slate-200 bg-white text-slate-700 text-xs font-normal hover:bg-slate-50 transition-colors shrink-0 shadow-xs cursor-pointer capitalize"
          >
            <span className="text-slate-600 font-bold text-lg leading-none mt-[-2px]">+</span>
            <span>{addLabel}</span>
          </button>
        )}
      </div>
    </div>
  );
};
